import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { 
  Settings, 
  User, 
  Lock, 
  Mail, 
  Globe, 
  CreditCard, 
  Bell, 
  Shield, 
  Activity,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  X
} from "lucide-react";

// Firebase imports
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase/config';

const PlatformSettings = ({ user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // États pour les différentes sections
  const [generalSettings, setGeneralSettings] = useState({
    platformName: 'CETROBON',
    platformDescription: 'Plateforme de réservation d\'ateliers culinaires',
    websiteUrl: 'https://cetrobon.com',
    supportEmail: 'support@cetrobon.com',
    contactPhone: '+33 1 23 45 67 89',
    defaultLanguage: 'fr',
    timezone: 'Europe/Paris',
    currency: 'EUR',
    taxRate: 20,
    platformCommission: 15,
    smtpHost: '',
    smtpPort: 587,
    smtpSecurity: 'tls',
    smtpUsername: '',
    smtpPassword: ''
  });

  const [paymentSettings, setPaymentSettings] = useState({
    stripeEnabled: true,
    stripePublicKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    stripeMode: 'test', // test ou production
    paypalEnabled: false,
    paypalClientId: '',
    paypalClientSecret: '',
    paypalWebhookId: '',
    paypalMode: 'sandbox', // sandbox ou production
    minAmount: 5,
    maxAmount: 500,
    refundEnabled: true,
    autoRefundDays: 7,
    paymentMethods: ['card', 'sepa'],
    supportedCurrencies: ['EUR', 'USD'],
    commissionType: 'percentage', // percentage ou fixed
    platformFee: 2.5
  });

  const [notificationSettings, setNotificationSettings] = useState({
    // Services Email
    emailProvider: 'sendgrid', // sendgrid, mailchimp, mailgun, postmark
    sendgridApiKey: '',
    sendgridFromEmail: '',
    sendgridFromName: '',
    mailchimpApiKey: '',
    mailchimpListId: '',
    mailgunApiKey: '',
    mailgunDomain: '',
    postmarkApiKey: '',
    
    // Services SMS
    smsProvider: 'twilio', // twilio, nexmo, ovh
    twilioAccountSid: '',
    twilioAuthToken: '',
    twilioPhoneNumber: '',
    nexmoApiKey: '',
    nexmoApiSecret: '',
    nexmoFromNumber: '',
    ovhApplicationKey: '',
    ovhApplicationSecret: '',
    ovhConsumerKey: '',
    
    // Notifications automatiques
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    adminAlerts: true,
    bookingNotifications: true,
    paymentNotifications: true,
    reminderEmails: true,
    
    // Newsletter et campagnes
    newsletterEnabled: true,
    autoSubscribeUsers: true,
    doubleOptIn: true,
    unsubscribeLink: true,
    trackOpens: true,
    trackClicks: true,
    
    // Templates d'email
    welcomeEmailEnabled: true,
    bookingConfirmationEnabled: true,
    paymentReceiptEnabled: true,
    reminderEmailEnabled: true,
    newsletterTemplateId: '',
    
    // Templates SMS
    bookingSmsEnabled: false,
    paymentSmsEnabled: false,
    reminderSmsEnabled: false
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    passwordMinLength: 8,
    passwordRequireSpecial: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    accountLockDuration: 15,
    ipWhitelistEnabled: false,
    auditLogsEnabled: true
  });

  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    debugMode: false,
    cacheEnabled: true,
    analyticsEnabled: true,
    errorReportingEnabled: true,
    performanceMonitoring: true,
    backupEnabled: true,
    backupFrequency: 'daily'
  });

  // États pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // États pour les modals
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    title: '',
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadPlatformSettings();
  }, []);

  const loadPlatformSettings = async () => {
    try {
      setLoading(true);
      
      // Charger les paramètres depuis Firestore
      const settingsRef = doc(db, 'platform-settings', 'default');
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        
        // Mettre à jour les états avec les données chargées
        if (data.general) setGeneralSettings(prev => ({ ...prev, ...data.general }));
        if (data.payment) setPaymentSettings(prev => ({ ...prev, ...data.payment }));
        if (data.notification) setNotificationSettings(prev => ({ ...prev, ...data.notification }));
        if (data.security) setSecuritySettings(prev => ({ ...prev, ...data.security }));
        if (data.system) setSystemSettings(prev => ({ ...prev, ...data.system }));
        
        console.log('✅ Paramètres de plateforme chargés depuis Firestore');
      } else {
        // Créer les paramètres par défaut
        await savePlatformSettings();
        console.log('✅ Paramètres par défaut créés');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      showAlertMessage('Erreur', 'Impossible de charger les paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };

  const savePlatformSettings = async () => {
    try {
      setLoading(true);
      
      const settingsRef = doc(db, 'platform-settings', 'default');
      const settingsData = {
        general: generalSettings,
        payment: paymentSettings,
        notification: notificationSettings,
        security: securitySettings,
        system: systemSettings,
        updated_at: new Date(),
        updated_by: user?.email || 'unknown'
      };
      
      await setDoc(settingsRef, settingsData);
      
      console.log('✅ Paramètres de plateforme sauvegardés dans Firestore');
      showAlertMessage('Succès', 'Paramètres sauvegardés avec succès !', 'success');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      showAlertMessage('Erreur', 'Erreur lors de la sauvegarde des paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showAlertMessage('Erreur', 'Tous les champs sont requis', 'error');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showAlertMessage('Erreur', 'La confirmation du mot de passe ne correspond pas', 'error');
      return;
    }
    
    if (passwordData.newPassword.length < securitySettings.passwordMinLength) {
      showAlertMessage('Erreur', `Le mot de passe doit contenir au moins ${securitySettings.passwordMinLength} caractères`, 'error');
      return;
    }

    try {
      setLoading(true);
      
      if (auth.currentUser) {
        // Ré-authentifier l'utilisateur
        const credential = EmailAuthProvider.credential(
          auth.currentUser.email, 
          passwordData.currentPassword
        );
        
        await reauthenticateWithCredential(auth.currentUser, credential);
        
        // Mettre à jour le mot de passe
        await updatePassword(auth.currentUser, passwordData.newPassword);
        
        // Réinitialiser le formulaire
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        console.log('✅ Mot de passe mis à jour avec succès');
        showAlertMessage('Succès', 'Mot de passe modifié avec succès !', 'success');
      }
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      let errorMessage = 'Erreur lors du changement de mot de passe';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe actuel incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le nouveau mot de passe est trop faible';
      }
      
      showAlertMessage('Erreur', errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      if (auth.currentUser?.email) {
        await sendPasswordResetEmail(auth, auth.currentUser.email);
        showAlertMessage('Succès', 'Email de réinitialisation envoyé !', 'success');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de réinitialisation:', error);
      showAlertMessage('Erreur', 'Erreur lors de l\'envoi de l\'email', 'error');
    }
  };

  const showAlertMessage = (title, message, type) => {
    setAlertData({ title, message, type });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  const togglePassword = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Paramètres de la Plateforme</h1>
            <p className="text-gray-600">Configurez tous les aspects de votre plateforme CETROBON</p>
          </div>
          <div className="flex space-x-3">
            <Button onClick={savePlatformSettings} className="bg-orange-600 hover:bg-orange-700">
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </Button>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              Fermer
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="general">
              <Globe className="w-4 h-4 mr-2" />
              Général
            </TabsTrigger>
            <TabsTrigger value="payment">
              <CreditCard className="w-4 h-4 mr-2" />
              Paiement
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="w-4 h-4 mr-2" />
              Sécurité
            </TabsTrigger>
            <TabsTrigger value="system">
              <Activity className="w-4 h-4 mr-2" />
              Système
            </TabsTrigger>
            <TabsTrigger value="account">
              <User className="w-4 h-4 mr-2" />
              Mon Compte
            </TabsTrigger>
          </TabsList>

          {/* Onglet Général */}
          <TabsContent value="general" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informations de la plateforme</CardTitle>
                  <CardDescription>Configurez les informations générales</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="platformName">Nom de la plateforme</Label>
                    <Input
                      id="platformName"
                      value={generalSettings.platformName}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, platformName: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="platformDescription">Description</Label>
                    <Input
                      id="platformDescription"
                      value={generalSettings.platformDescription}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, platformDescription: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="websiteUrl">URL du site web</Label>
                    <Input
                      id="websiteUrl"
                      value={generalSettings.websiteUrl}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, websiteUrl: e.target.value}))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Contact et Support</CardTitle>
                  <CardDescription>Informations de contact</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="supportEmail">Email de support</Label>
                    <Input
                      id="supportEmail"
                      type="email"
                      value={generalSettings.supportEmail}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, supportEmail: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPhone">Téléphone de contact</Label>
                    <Input
                      id="contactPhone"
                      value={generalSettings.contactPhone}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, contactPhone: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="timezone">Fuseau horaire</Label>
                    <select
                      id="timezone"
                      value={generalSettings.timezone}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, timezone: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Europe/London">Europe/London</option>
                      <option value="America/New_York">America/New_York</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Configuration financière</CardTitle>
                  <CardDescription>Paramètres de facturation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="currency">Devise</Label>
                    <select
                      id="currency"
                      value={generalSettings.currency}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, currency: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="EUR">Euro (€)</option>
                      <option value="USD">Dollar ($)</option>
                      <option value="GBP">Livre Sterling (£)</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="taxRate">Taux de TVA (%)</Label>
                    <Input
                      id="taxRate"
                      type="number"
                      value={generalSettings.taxRate}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, taxRate: parseFloat(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="platformCommission">Commission plateforme (%)</Label>
                    <Input
                      id="platformCommission"
                      type="number"
                      value={generalSettings.platformCommission}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, platformCommission: parseFloat(e.target.value)}))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Configuration Email (SMTP)</CardTitle>
                  <CardDescription>Paramètres pour l'envoi d'emails</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="smtpHost">Serveur SMTP</Label>
                    <Input
                      id="smtpHost"
                      placeholder="smtp.gmail.com"
                      value={generalSettings.smtpHost || ''}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, smtpHost: e.target.value}))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtpPort">Port SMTP</Label>
                      <Input
                        id="smtpPort"
                        type="number"
                        placeholder="587"
                        value={generalSettings.smtpPort || ''}
                        onChange={(e) => setGeneralSettings(prev => ({...prev, smtpPort: parseInt(e.target.value)}))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtpSecurity">Sécurité</Label>
                      <select
                        id="smtpSecurity"
                        value={generalSettings.smtpSecurity || 'tls'}
                        onChange={(e) => setGeneralSettings(prev => ({...prev, smtpSecurity: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="none">Aucune</option>
                        <option value="tls">TLS</option>
                        <option value="ssl">SSL</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="smtpUsername">Nom d'utilisateur SMTP</Label>
                    <Input
                      id="smtpUsername"
                      placeholder="votre-email@exemple.com"
                      value={generalSettings.smtpUsername || ''}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, smtpUsername: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtpPassword">Mot de passe SMTP</Label>
                    <Input
                      id="smtpPassword"
                      type="password"
                      placeholder="Votre mot de passe ou app password"
                      value={generalSettings.smtpPassword || ''}
                      onChange={(e) => setGeneralSettings(prev => ({...prev, smtpPassword: e.target.value}))}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pour Gmail, utilisez un mot de passe d'application
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      showAlertMessage('Test', 'Test de l\'envoi d\'email en cours...', 'success');
                      // TODO: Implémenter test d'envoi d'email
                    }}
                    disabled={!generalSettings.smtpHost || !generalSettings.smtpUsername}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Tester la configuration email
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onglet Paiement */}
          <TabsContent value="payment" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Configuration Stripe */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="w-6 h-6 bg-blue-600 rounded mr-2 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">S</span>
                    </div>
                    Configuration Stripe
                  </CardTitle>
                  <CardDescription>Configurez votre intégration Stripe</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="stripeEnabled">Stripe activé</Label>
                    <Switch
                      id="stripeEnabled"
                      checked={paymentSettings.stripeEnabled}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({...prev, stripeEnabled: checked}))}
                    />
                  </div>
                  
                  {paymentSettings.stripeEnabled && (
                    <>
                      <div>
                        <Label htmlFor="stripeMode">Mode Stripe</Label>
                        <select
                          id="stripeMode"
                          value={paymentSettings.stripeMode}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, stripeMode: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="test">Test (développement)</option>
                          <option value="production">Production (live)</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label htmlFor="stripePublicKey">Clé publique Stripe</Label>
                        <Input
                          id="stripePublicKey"
                          type="text"
                          placeholder={paymentSettings.stripeMode === 'test' ? 'pk_test_...' : 'pk_live_...'}
                          value={paymentSettings.stripePublicKey}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, stripePublicKey: e.target.value}))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Disponible dans votre Dashboard Stripe → Développeurs → Clés API
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="stripeSecretKey">Clé secrète Stripe</Label>
                        <Input
                          id="stripeSecretKey"
                          type="password"
                          placeholder={paymentSettings.stripeMode === 'test' ? 'sk_test_...' : 'sk_live_...'}
                          value={paymentSettings.stripeSecretKey}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, stripeSecretKey: e.target.value}))}
                        />
                        <p className="text-xs text-red-500 mt-1">
                          ⚠️ Gardez cette clé secrète ! Ne la partagez jamais.
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="stripeWebhookSecret">Webhook Secret Stripe</Label>
                        <Input
                          id="stripeWebhookSecret"
                          type="password"
                          placeholder="whsec_..."
                          value={paymentSettings.stripeWebhookSecret}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, stripeWebhookSecret: e.target.value}))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          URL webhook : https://votre-domaine.com/api/webhooks/stripe
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Configuration PayPal */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="w-6 h-6 bg-blue-500 rounded mr-2 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    Configuration PayPal
                  </CardTitle>
                  <CardDescription>Configurez votre intégration PayPal</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="paypalEnabled">PayPal activé</Label>
                    <Switch
                      id="paypalEnabled"
                      checked={paymentSettings.paypalEnabled}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({...prev, paypalEnabled: checked}))}
                    />
                  </div>
                  
                  {paymentSettings.paypalEnabled && (
                    <>
                      <div>
                        <Label htmlFor="paypalMode">Mode PayPal</Label>
                        <select
                          id="paypalMode"
                          value={paymentSettings.paypalMode}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, paypalMode: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="sandbox">Sandbox (test)</option>
                          <option value="production">Production (live)</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label htmlFor="paypalClientId">Client ID PayPal</Label>
                        <Input
                          id="paypalClientId"
                          type="text"
                          placeholder="Votre Client ID PayPal"
                          value={paymentSettings.paypalClientId}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, paypalClientId: e.target.value}))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Disponible dans PayPal Developer → Mes applications
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="paypalClientSecret">Client Secret PayPal</Label>
                        <Input
                          id="paypalClientSecret"
                          type="password"
                          placeholder="Votre Client Secret PayPal"
                          value={paymentSettings.paypalClientSecret}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, paypalClientSecret: e.target.value}))}
                        />
                        <p className="text-xs text-red-500 mt-1">
                          ⚠️ Gardez ce secret confidentiel !
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="paypalWebhookId">Webhook ID PayPal</Label>
                        <Input
                          id="paypalWebhookId"
                          type="text"
                          placeholder="Votre Webhook ID"
                          value={paymentSettings.paypalWebhookId}
                          onChange={(e) => setPaymentSettings(prev => ({...prev, paypalWebhookId: e.target.value}))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          URL webhook : https://votre-domaine.com/api/webhooks/paypal
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Limites et Commission */}
              <Card>
                <CardHeader>
                  <CardTitle>Limites de paiement</CardTitle>
                  <CardDescription>Montants et restrictions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="minAmount">Montant minimum (€)</Label>
                    <Input
                      id="minAmount"
                      type="number"
                      value={paymentSettings.minAmount}
                      onChange={(e) => setPaymentSettings(prev => ({...prev, minAmount: parseFloat(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxAmount">Montant maximum (€)</Label>
                    <Input
                      id="maxAmount"
                      type="number"
                      value={paymentSettings.maxAmount}
                      onChange={(e) => setPaymentSettings(prev => ({...prev, maxAmount: parseFloat(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="platformFee">Frais de plateforme (%)</Label>
                    <Input
                      id="platformFee"
                      type="number"
                      step="0.1"
                      value={paymentSettings.platformFee}
                      onChange={(e) => setPaymentSettings(prev => ({...prev, platformFee: parseFloat(e.target.value)}))}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Frais prélevés sur chaque transaction
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Remboursements */}
              <Card>
                <CardHeader>
                  <CardTitle>Remboursements</CardTitle>
                  <CardDescription>Politique de remboursement</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="refundEnabled">Remboursements activés</Label>
                    <Switch
                      id="refundEnabled"
                      checked={paymentSettings.refundEnabled}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({...prev, refundEnabled: checked}))}
                    />
                  </div>
                  
                  {paymentSettings.refundEnabled && (
                    <div>
                      <Label htmlFor="autoRefundDays">Remboursement automatique (jours)</Label>
                      <Input
                        id="autoRefundDays"
                        type="number"
                        value={paymentSettings.autoRefundDays}
                        onChange={(e) => setPaymentSettings(prev => ({...prev, autoRefundDays: parseInt(e.target.value)}))}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Délai pour demander un remboursement automatique
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <Label>Devises supportées</Label>
                    <div className="flex space-x-2 mt-2">
                      {['EUR', 'USD', 'GBP', 'CAD'].map(currency => (
                        <div key={currency} className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            id={`currency-${currency}`}
                            checked={paymentSettings.supportedCurrencies.includes(currency)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPaymentSettings(prev => ({
                                  ...prev,
                                  supportedCurrencies: [...prev.supportedCurrencies, currency]
                                }));
                              } else {
                                setPaymentSettings(prev => ({
                                  ...prev,
                                  supportedCurrencies: prev.supportedCurrencies.filter(c => c !== currency)
                                }));
                              }
                            }}
                          />
                          <Label htmlFor={`currency-${currency}`} className="text-sm">{currency}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Test de Connexion */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Test de Connexion</CardTitle>
                  <CardDescription>Vérifiez que vos configurations de paiement sont correctes</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex space-x-4">
                    {paymentSettings.stripeEnabled && (
                      <Button 
                        variant="outline" 
                        onClick={async () => {
                          showAlertMessage('Test', 'Test de connexion Stripe en cours...', 'success');
                          // TODO: Implémenter test de connexion Stripe
                        }}
                        disabled={!paymentSettings.stripePublicKey || !paymentSettings.stripeSecretKey}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Tester Stripe
                      </Button>
                    )}
                    {paymentSettings.paypalEnabled && (
                      <Button 
                        variant="outline" 
                        onClick={async () => {
                          showAlertMessage('Test', 'Test de connexion PayPal en cours...', 'success');
                          // TODO: Implémenter test de connexion PayPal
                        }}
                        disabled={!paymentSettings.paypalClientId || !paymentSettings.paypalClientSecret}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Tester PayPal
                      </Button>
                    )}
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-yellow-800">Instructions importantes</h4>
                        <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                          <li>• Pour Stripe : Configurez d'abord vos webhooks dans le Dashboard Stripe</li>
                          <li>• Pour PayPal : Créez une application dans PayPal Developer Console</li>
                          <li>• Utilisez toujours les modes test avant de passer en production</li>
                          <li>• Sauvegardez vos paramètres avant de tester les connexions</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onglet Notifications */}
          <TabsContent value="notifications" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Configuration Email */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    Configuration Email
                  </CardTitle>
                  <CardDescription>Configurez votre service d'envoi d'emails</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="emailProvider">Fournisseur Email</Label>
                    <select
                      id="emailProvider"
                      value={notificationSettings.emailProvider}
                      onChange={(e) => setNotificationSettings(prev => ({...prev, emailProvider: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="sendgrid">SendGrid</option>
                      <option value="mailchimp">Mailchimp</option>
                      <option value="mailgun">Mailgun</option>
                      <option value="postmark">Postmark</option>
                    </select>
                  </div>

                  {notificationSettings.emailProvider === 'sendgrid' && (
                    <>
                      <div>
                        <Label htmlFor="sendgridApiKey">Clé API SendGrid</Label>
                        <Input
                          id="sendgridApiKey"
                          type="password"
                          placeholder="SG.xxx"
                          value={notificationSettings.sendgridApiKey}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, sendgridApiKey: e.target.value}))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Disponible dans SendGrid → Settings → API Keys
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="sendgridFromEmail">Email expéditeur</Label>
                        <Input
                          id="sendgridFromEmail"
                          type="email"
                          placeholder="noreply@cetrobon.com"
                          value={notificationSettings.sendgridFromEmail}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, sendgridFromEmail: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="sendgridFromName">Nom expéditeur</Label>
                        <Input
                          id="sendgridFromName"
                          placeholder="CETROBON"
                          value={notificationSettings.sendgridFromName}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, sendgridFromName: e.target.value}))}
                        />
                      </div>
                    </>
                  )}

                  {notificationSettings.emailProvider === 'mailchimp' && (
                    <>
                      <div>
                        <Label htmlFor="mailchimpApiKey">Clé API Mailchimp</Label>
                        <Input
                          id="mailchimpApiKey"
                          type="password"
                          placeholder="xxx-us1"
                          value={notificationSettings.mailchimpApiKey}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, mailchimpApiKey: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="mailchimpListId">ID de la liste</Label>
                        <Input
                          id="mailchimpListId"
                          placeholder="abcd123456"
                          value={notificationSettings.mailchimpListId}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, mailchimpListId: e.target.value}))}
                        />
                      </div>
                    </>
                  )}

                  {notificationSettings.emailProvider === 'mailgun' && (
                    <>
                      <div>
                        <Label htmlFor="mailgunApiKey">Clé API Mailgun</Label>
                        <Input
                          id="mailgunApiKey"
                          type="password"
                          placeholder="key-xxx"
                          value={notificationSettings.mailgunApiKey}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, mailgunApiKey: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="mailgunDomain">Domaine Mailgun</Label>
                        <Input
                          id="mailgunDomain"
                          placeholder="mg.cetrobon.com"
                          value={notificationSettings.mailgunDomain}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, mailgunDomain: e.target.value}))}
                        />
                      </div>
                    </>
                  )}

                  {notificationSettings.emailProvider === 'postmark' && (
                    <div>
                      <Label htmlFor="postmarkApiKey">Token API Postmark</Label>
                      <Input
                        id="postmarkApiKey"
                        type="password"
                        placeholder="xxx-xxx-xxx"
                        value={notificationSettings.postmarkApiKey}
                        onChange={(e) => setNotificationSettings(prev => ({...prev, postmarkApiKey: e.target.value}))}
                      />
                    </div>
                  )}

                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      showAlertMessage('Test', 'Test de connexion email en cours...', 'success');
                      // TODO: Implémenter test de connexion email
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Tester la configuration
                  </Button>
                </CardContent>
              </Card>

              {/* Configuration SMS */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="w-5 h-5 bg-green-600 rounded mr-2 flex items-center justify-center">
                      <span className="text-white text-xs">📱</span>
                    </div>
                    Configuration SMS
                  </CardTitle>
                  <CardDescription>Configurez votre service d'envoi de SMS</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="smsProvider">Fournisseur SMS</Label>
                    <select
                      id="smsProvider"
                      value={notificationSettings.smsProvider}
                      onChange={(e) => setNotificationSettings(prev => ({...prev, smsProvider: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="twilio">Twilio</option>
                      <option value="nexmo">Vonage (Nexmo)</option>
                      <option value="ovh">OVH</option>
                    </select>
                  </div>

                  {notificationSettings.smsProvider === 'twilio' && (
                    <>
                      <div>
                        <Label htmlFor="twilioAccountSid">Account SID Twilio</Label>
                        <Input
                          id="twilioAccountSid"
                          placeholder="ACxxxx"
                          value={notificationSettings.twilioAccountSid}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, twilioAccountSid: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="twilioAuthToken">Auth Token Twilio</Label>
                        <Input
                          id="twilioAuthToken"
                          type="password"
                          placeholder="Votre token d'authentification"
                          value={notificationSettings.twilioAuthToken}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, twilioAuthToken: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="twilioPhoneNumber">Numéro Twilio</Label>
                        <Input
                          id="twilioPhoneNumber"
                          placeholder="+33612345678"
                          value={notificationSettings.twilioPhoneNumber}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, twilioPhoneNumber: e.target.value}))}
                        />
                      </div>
                    </>
                  )}

                  {notificationSettings.smsProvider === 'nexmo' && (
                    <>
                      <div>
                        <Label htmlFor="nexmoApiKey">Clé API Vonage</Label>
                        <Input
                          id="nexmoApiKey"
                          placeholder="abcd1234"
                          value={notificationSettings.nexmoApiKey}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, nexmoApiKey: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="nexmoApiSecret">Secret API Vonage</Label>
                        <Input
                          id="nexmoApiSecret"
                          type="password"
                          placeholder="secret123"
                          value={notificationSettings.nexmoApiSecret}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, nexmoApiSecret: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="nexmoFromNumber">Numéro expéditeur</Label>
                        <Input
                          id="nexmoFromNumber"
                          placeholder="CETROBON ou +33612345678"
                          value={notificationSettings.nexmoFromNumber}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, nexmoFromNumber: e.target.value}))}
                        />
                      </div>
                    </>
                  )}

                  {notificationSettings.smsProvider === 'ovh' && (
                    <>
                      <div>
                        <Label htmlFor="ovhApplicationKey">Application Key OVH</Label>
                        <Input
                          id="ovhApplicationKey"
                          placeholder="Votre clé d'application"
                          value={notificationSettings.ovhApplicationKey}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, ovhApplicationKey: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ovhApplicationSecret">Application Secret OVH</Label>
                        <Input
                          id="ovhApplicationSecret"
                          type="password"
                          placeholder="Votre secret d'application"
                          value={notificationSettings.ovhApplicationSecret}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, ovhApplicationSecret: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ovhConsumerKey">Consumer Key OVH</Label>
                        <Input
                          id="ovhConsumerKey"
                          type="password"
                          placeholder="Votre consumer key"
                          value={notificationSettings.ovhConsumerKey}
                          onChange={(e) => setNotificationSettings(prev => ({...prev, ovhConsumerKey: e.target.value}))}
                        />
                      </div>
                    </>
                  )}

                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      showAlertMessage('Test', 'Test de connexion SMS en cours...', 'success');
                      // TODO: Implémenter test de connexion SMS
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Tester la configuration
                  </Button>
                </CardContent>
              </Card>

              {/* Notifications automatiques */}
              <Card>
                <CardHeader>
                  <CardTitle>Notifications automatiques</CardTitle>
                  <CardDescription>Activez les notifications selon les événements</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="emailNotifications">Notifications email</Label>
                    <Switch
                      id="emailNotifications"
                      checked={notificationSettings.emailNotifications}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, emailNotifications: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="smsNotifications">Notifications SMS</Label>
                    <Switch
                      id="smsNotifications"
                      checked={notificationSettings.smsNotifications}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, smsNotifications: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="bookingNotifications">Réservations</Label>
                    <Switch
                      id="bookingNotifications"
                      checked={notificationSettings.bookingNotifications}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, bookingNotifications: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="paymentNotifications">Paiements</Label>
                    <Switch
                      id="paymentNotifications"
                      checked={notificationSettings.paymentNotifications}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, paymentNotifications: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="reminderEmails">Rappels</Label>
                    <Switch
                      id="reminderEmails"
                      checked={notificationSettings.reminderEmails}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, reminderEmails: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="adminAlerts">Alertes admin</Label>
                    <Switch
                      id="adminAlerts"
                      checked={notificationSettings.adminAlerts}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, adminAlerts: checked}))}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Newsletter et campagnes */}
              <Card>
                <CardHeader>
                  <CardTitle>Newsletter et Marketing</CardTitle>
                  <CardDescription>Configuration des campagnes marketing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="newsletterEnabled">Newsletter activée</Label>
                    <Switch
                      id="newsletterEnabled"
                      checked={notificationSettings.newsletterEnabled}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, newsletterEnabled: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="autoSubscribeUsers">Inscription automatique</Label>
                    <Switch
                      id="autoSubscribeUsers"
                      checked={notificationSettings.autoSubscribeUsers}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, autoSubscribeUsers: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="doubleOptIn">Double opt-in</Label>
                    <Switch
                      id="doubleOptIn"
                      checked={notificationSettings.doubleOptIn}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, doubleOptIn: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="trackOpens">Suivi des ouvertures</Label>
                    <Switch
                      id="trackOpens"
                      checked={notificationSettings.trackOpens}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, trackOpens: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="trackClicks">Suivi des clics</Label>
                    <Switch
                      id="trackClicks"
                      checked={notificationSettings.trackClicks}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({...prev, trackClicks: checked}))}
                    />
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <Button 
                      className="w-full" 
                      onClick={() => {
                        // Fermer PlatformSettings et ouvrir CampaignManager
                        onClose();
                        // Cette action sera gérée par le composant parent
                        showAlertMessage('Info', 'Redirection vers le gestionnaire de campagnes...', 'success');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('openCampaignManager'));
                        }, 500);
                      }}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Gérer les campagnes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onglet Sécurité */}
          <TabsContent value="security" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Politique de mot de passe</CardTitle>
                  <CardDescription>Configurez les exigences de sécurité</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="passwordMinLength">Longueur minimum du mot de passe</Label>
                    <Input
                      id="passwordMinLength"
                      type="number"
                      value={securitySettings.passwordMinLength}
                      onChange={(e) => setSecuritySettings(prev => ({...prev, passwordMinLength: parseInt(e.target.value)}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="passwordRequireSpecial">Caractères spéciaux requis</Label>
                    <Switch
                      id="passwordRequireSpecial"
                      checked={securitySettings.passwordRequireSpecial}
                      onCheckedChange={(checked) => setSecuritySettings(prev => ({...prev, passwordRequireSpecial: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="twoFactorAuth">Authentification à deux facteurs</Label>
                    <Switch
                      id="twoFactorAuth"
                      checked={securitySettings.twoFactorAuth}
                      onCheckedChange={(checked) => setSecuritySettings(prev => ({...prev, twoFactorAuth: checked}))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Session et accès</CardTitle>
                  <CardDescription>Paramètres de session utilisateur</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="sessionTimeout">Timeout de session (minutes)</Label>
                    <Input
                      id="sessionTimeout"
                      type="number"
                      value={securitySettings.sessionTimeout}
                      onChange={(e) => setSecuritySettings(prev => ({...prev, sessionTimeout: parseInt(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxLoginAttempts">Tentatives de connexion max</Label>
                    <Input
                      id="maxLoginAttempts"
                      type="number"
                      value={securitySettings.maxLoginAttempts}
                      onChange={(e) => setSecuritySettings(prev => ({...prev, maxLoginAttempts: parseInt(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="accountLockDuration">Durée de blocage (minutes)</Label>
                    <Input
                      id="accountLockDuration"
                      type="number"
                      value={securitySettings.accountLockDuration}
                      onChange={(e) => setSecuritySettings(prev => ({...prev, accountLockDuration: parseInt(e.target.value)}))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onglet Système */}
          <TabsContent value="system" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Maintenance et Debug</CardTitle>
                  <CardDescription>Outils de maintenance système</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="maintenanceMode">Mode maintenance</Label>
                    <Switch
                      id="maintenanceMode"
                      checked={systemSettings.maintenanceMode}
                      onCheckedChange={(checked) => setSystemSettings(prev => ({...prev, maintenanceMode: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="debugMode">Mode debug</Label>
                    <Switch
                      id="debugMode"
                      checked={systemSettings.debugMode}
                      onCheckedChange={(checked) => setSystemSettings(prev => ({...prev, debugMode: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="cacheEnabled">Cache activé</Label>
                    <Switch
                      id="cacheEnabled"
                      checked={systemSettings.cacheEnabled}
                      onCheckedChange={(checked) => setSystemSettings(prev => ({...prev, cacheEnabled: checked}))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Monitoring et Analytics</CardTitle>
                  <CardDescription>Suivi des performances</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="analyticsEnabled">Analytics activé</Label>
                    <Switch
                      id="analyticsEnabled"
                      checked={systemSettings.analyticsEnabled}
                      onCheckedChange={(checked) => setSystemSettings(prev => ({...prev, analyticsEnabled: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="errorReportingEnabled">Rapport d'erreurs</Label>
                    <Switch
                      id="errorReportingEnabled"
                      checked={systemSettings.errorReportingEnabled}
                      onCheckedChange={(checked) => setSystemSettings(prev => ({...prev, errorReportingEnabled: checked}))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="performanceMonitoring">Monitoring des performances</Label>
                    <Switch
                      id="performanceMonitoring"
                      checked={systemSettings.performanceMonitoring}
                      onCheckedChange={(checked) => setSystemSettings(prev => ({...prev, performanceMonitoring: checked}))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onglet Mon Compte */}
          <TabsContent value="account" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informations du compte</CardTitle>
                  <CardDescription>Vos informations personnelles</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Nom d'utilisateur</Label>
                    <Input value={user?.name || ''} disabled />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={user?.email || ''} disabled />
                  </div>
                  <div>
                    <Label>Rôle</Label>
                    <div className="pt-2">
                      <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'}>
                        {user?.role || 'Utilisateur'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Changer le mot de passe</CardTitle>
                  <CardDescription>Modifiez votre mot de passe de connexion</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showPasswords.current ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({...prev, currentPassword: e.target.value}))}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => togglePassword('current')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showPasswords.new ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({...prev, newPassword: e.target.value}))}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => togglePassword('new')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showPasswords.confirm ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({...prev, confirmPassword: e.target.value}))}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => togglePassword('confirm')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex space-x-3">
                      <Button type="submit" disabled={loading}>
                        <Lock className="w-4 h-4 mr-2" />
                        Changer le mot de passe
                      </Button>
                      <Button type="button" variant="outline" onClick={handleResetPassword}>
                        <Mail className="w-4 h-4 mr-2" />
                        Envoyer email de réinitialisation
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Alert Modal */}
      {showAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              {alertData.type === 'success' ? (
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              )}
              <h3 className="text-lg font-semibold">{alertData.title}</h3>
            </div>
            <p className="text-gray-600 mb-4">{alertData.message}</p>
            <div className="flex justify-end">
              <Button onClick={() => setShowAlert(false)}>
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSettings;